import { useState, useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import InvoiceSituation from '../features/invoice/InvoiceSituation';
import '../styles/global.css';
import '../styles/nav.css';
import MovementRecord from '../features/movement/MovementRecords';
import SupplyRecord from '../features/supply/SupplyRecords';
import Sale from '../features/sale/sales/Sale';
import ClientSituation from '../features/client/Situation';
import Wallet from '../features/wallet/Wallet';
import WalletList from '../features/wallet/WalletList';
import Expense from '../features/expense/Expense';
import Voucher from '../features/voucher/Voucher';
import Offer from '../features/offer/Offer';
import SupplierSituation from '../features/supplier/Situation';
import Payment from '../features/payments/Payment';
import SupplyPayment from '../features/payments/SupplyPayment';
import Deposit from '../features/deposit/Deposit';
import Pending from '../features/deposit/Pending';
import TopBar from '../layouts/TopBar';
import { useLocation, useNavigate } from 'react-router-dom';
import { faCashRegister, faChartLine, faCubesStacked, faFileContract, faMoneyBill, faPeopleGroup, faSackDollar, faVault } from '@fortawesome/free-solid-svg-icons';
import NavItemLink from '../components/NavItemLink';
import BottomBar from '../layouts/BottomBar';
import PointOfSale from '../features/sale/PointOfSale';
import { MoveProvider } from '../features/movement/MoveContext';
import { SupplyProvider } from '../features/supply/SupplyContext';
import { SaleProvider } from '../features/sale/SaleContext';
import { useAuth } from '../hooks/useAuth';
import OfflineScreen from '../components/OfflineScreen';
import { useAppSocket } from '../providers/AppSocketContext';
import { useUI } from '../providers/MoleUIContext';
import Helmetify from '../components/Helmetify';
import { allows, anyOf, canSeeDashboard, type GrantSpec, reads, records, situation } from '../util/access';
import ConversionRecord from '../features/conversion/ConversionRecords';
import { ConversionProvider } from '../features/conversion/ConversionContext';
import ProductionRecords from '../features/production/ProductionRecords';
import { ProductionProvider } from '../features/production/ProductionContext';
import BomRecords from '../features/production/BomRecords';
import POSGrid from '../features/sale/POSGrid';
import SupplyListing from '../features/supply/SupplyListing';
import SupplySituation from '../features/supply/SupplySituation';
import MovementListing from '../features/movement/MovementListing';
import { InventoryProvider } from '../features/inventory/InventoryContext';
import InventoryRecord from '../features/inventory/InventoryRecords';
import CurrentInventory from '../features/inventory/current/Inventory';
import MovementTracing from '../features/movement/MovementTracing';
import ReportsDashboard from '../features/reports/ReportsDashboard';
import MovementTracingChrono from '../features/movement/MovementTracingChrono';
import CurrentInventoryVisual from '../features/inventory/current/InventoryVisual';
import ProductCatalog from '../features/inventory/ProductCatalog';
import { useTranslation } from 'react-i18next';
import AgentSituation from '../features/client/AgentSituation';
import Dashboard from '../features/dashboard/Dashboard';
import {
	SalesCategoryDaily,
	SalesDepotDaily,
	SalesFamilyDaily,
	SalesSectorDaily,
	SalesUserDaily,
} from '../features/sale/sales/DailyBreakdownPages';
import SalesItemDaily from '../features/sale/sales/SalesItemDaily';
import {
	PaymentDepotDaily,
	PaymentModeDaily,
	PaymentTypeDaily,
	PaymentUserDaily,
} from '../features/payments/DailyBreakdownPages';
import {
	SupplyPaymentDepotDaily,
	SupplyPaymentModeDaily,
	SupplyPaymentUserDaily,
} from '../features/payments/SupplyPaymentDailyBreakdownPages';
import PeopleHub from '../features/hubs/PeopleHub';
import AddressBook from '../features/client/AddressBook';
import CashHub from '../features/hubs/CashHub';
import StockHub from '../features/hubs/StockHub';
import {
	CASH_TABS,
	PEOPLE_TABS,
	REPORT_LIVE_TABS,
	STOCK_MENU_GRANTS,
	STOCK_TABS,
	canAccessCashHub,
	canAccessPeopleHub,
	canAccessReportsHub,
	canAccessStockHub,
} from '../features/hubs/hubAccess';

function Main() {
	const [activeTab, setActiveTab] = useState<string>(''); // State to track the active tab
	const [sideMenuMinimized, setSideMenuMinimized] = useState(false);
	const activeTabRef = useRef<string>("");
	const { session, accessVersion, access, logout } = useAuth();
	const { connected } = useAppSocket();
	const { loginModalOpen } = useUI();
	const { t } = useTranslation();



	const navigate = useNavigate();
	const location = useLocation();

	const TAB_ROUTES: Record<string, string> = {
		'0': '/dashboard',
		'1': '/pos/terminals',
		'1.1': '/pos',
		'2': '/invoice/situation',
		'3': '/payments',
		'3.1': '/payments/mode',
		'3.2': '/payments/type',
		'3.3': '/payments/user',
		'3.4': '/payments/depot',
		'15': '/supply-payments',
		'15.1': '/supply-payments/mode',
		'15.2': '/supply-payments/user',
		'15.3': '/supply-payments/depot',
		'17': '/expenses',
		'18': '/vouchers',
		'19': '/offers',
		'4': '/sales',
		'4.1': '/sales/item',
		'4.2': '/sales/family',
		'4.3': '/sales/category',
		'4.4': '/sales/depot',
		'4.5': '/sales/sector',
		'4.6': '/sales/user',
		'5': '/clients',
		'5.1': '/clients/wallets',
		'5.2': '/clients/wallets/transactions',
		'5.3': '/clients/addresses',
		'6': '/deposits',
		'7': '/deposits/pending',
		'8': '/stock/movement',
		'8.1': '/stock/movement/list',
		'8.2': '/stock/movement/trace',
		'8.3': '/stock/movement/trace/chrono',
		'9': '/stock/supply',
		'9.1': '/stock/supply/list',
		'9.2': '/stock/supply/situation',
		'10': '/stock/inventory',
		'10.1': '/stock/inventory/current',
		'10.2': '/stock/inventory/current/visual',
		'10.3': '/stock/inventory/product-catalog',
		'11': '/stock/conversion',
		'16': '/stock/production',
		'16.1': '/stock/production/boms',
		'12': '/reports',
		'13': '/agents',
		'14': '/suppliers',
		'20': '/people',
		'21': '/cash',
		'22': '/stock',
	};

	const TAB_TITLES: Record<string, string> = {
		'0': t('nav:tabs.dashboard'),
		'1': t('nav:tabs.pos_terminals'),
		'1.1': t('nav:tabs.pos'),
		'2': t('nav:tabs.invoice_situation'),
		'3': t('nav:tabs.payments'),
		'3.1': t('nav:tabs.daily_payments_mode'),
		'3.2': t('nav:tabs.daily_payments_type'),
		'3.3': t('nav:tabs.daily_payments_user'),
		'3.4': t('nav:tabs.daily_payments_depot'),
		'15': t('nav:tabs.supply_payments'),
		'15.1': t('nav:tabs.daily_supply_payments_mode'),
		'15.2': t('nav:tabs.daily_supply_payments_user'),
		'15.3': t('nav:tabs.daily_supply_payments_depot'),
		'17': t('nav:tabs.expenses'),
		'18': t('nav:tabs.vouchers'),
		'19': t('nav:tabs.offers'),
		'4': t('nav:tabs.sales'),
		'4.1': t('nav:tabs.daily_sales_item'),
		'4.2': t('nav:tabs.daily_sales_family'),
		'4.3': t('nav:tabs.daily_sales_category'),
		'4.4': t('nav:tabs.daily_sales_depot'),
		'4.5': t('nav:tabs.daily_sales_sector'),
		'4.6': t('nav:tabs.daily_sales_user'),
		'5': t('nav:tabs.clients'),
		'5.1': t('nav:tabs.clientWallets'),
		'5.2': t('nav:tabs.walletTransactions'),
		'5.3': t('nav:tabs.clientAddresses'),
		'13': t('nav:tabs.agents'),
		'14': t('nav:tabs.suppliers'),
		'6': t('nav:tabs.deposits'),
		'7': t('nav:tabs.pending_deposits'),
		'8': t('nav:tabs.stock_movement'),
		'8.1': t('nav:tabs.stock_movement_listing'),
		'8.2': t('nav:tabs.stock_movement_tracing'),
		'8.3': t('nav:tabs.stock_movement_chrono'),
		'9': t('nav:tabs.supply'),
		'9.1': t('nav:tabs.supply_listing'),
		'9.2': t('nav:tabs.supply_situation'),
		'10': t('nav:tabs.inventory'),
		'10.1': t('nav:tabs.current_inventory'),
		'10.2': t('nav:tabs.current_inventory_visual'),
		'10.3': t('nav:tabs.product_catalog'),
		'11': t('nav:tabs.stock_conversion'),
		'16': t('nav:tabs.production'),
		'16.1': t('nav:tabs.production_boms'),
		'12': t('nav:tabs.reports'),
		'20': t('nav:tabs.people'),
		'21': t('nav:tabs.cash'),
		'22': t('nav:tabs.stock'),
	};

	// What each menu entry asks for. The two tracing tabs were one
	// permission and are two grants now: the trace and its chronology.
	const TAB_GRANTS: Record<string, GrantSpec> = {
		'0': reads('dashboard'),
		'1': records('sale'),
		'2': situation('invoice'),
		'3': reads('payment'),
		'3.1': reads('payment'),
		'3.2': reads('payment'),
		'3.3': reads('payment'),
		'3.4': reads('payment'),
		'15': reads('supply_payment'),
		'15.1': reads('supply_payment'),
		'15.2': reads('supply_payment'),
		'15.3': reads('supply_payment'),
		'17': reads('expense'),
		'18': reads('voucher'),
		'19': reads('offer'),
		'4': anyOf(reads('sale'), reads('return')),
		'4.1': anyOf(reads('sale'), reads('return')),
		'4.2': anyOf(reads('sale'), reads('return')),
		'4.3': anyOf(reads('sale'), reads('return')),
		'4.4': anyOf(reads('sale'), reads('return')),
		'4.5': anyOf(reads('sale'), reads('return')),
		'4.6': anyOf(reads('sale'), reads('return')),
		'5': situation('client'),
		'5.1': reads('wallet'),
		'5.2': reads('wallet'),
		'5.3': reads('client_address'),
		'13': situation('agent'),
		'14': situation('supplier'),
		'6': reads('deposit'),
		'7': reads('deposit'),
		'8': reads('movement'),
		'8.1': reads('movement'),
		'8.2': reads('stock_tracing'),
		'8.3': reads('stock_timeline'),
		'9': reads('supply'),
		'9.1': reads('supply'),
		'9.2': situation('supply'),
		'10': reads('inventory'),
		'10.1': reads('inventory_current'),
		'10.2': reads('inventory_current'),
		'10.3': STOCK_MENU_GRANTS['10.3'],
		'11': reads('conversion'),
		'16': reads('production'),
		'16.1': STOCK_MENU_GRANTS['16.1'],
		'12': reads('report'),
	};

	const principal = { access };
	const canViewDashboard = canSeeDashboard(access);
	const canViewReports = canAccessReportsHub(access);
	const canViewPeople = canAccessPeopleHub(access);
	const canViewCash = canAccessCashHub(access);
	const canViewStock = canAccessStockHub(access);

	// Set the default tab on initial load based on access grants.
	useEffect(() => {
		if (activeTab === '' && session) {
			if (canSeeDashboard(access)) {
				activeTabRef.current = '0';
				setActiveTab('0');
			} else if (allows(principal, TAB_GRANTS['1'])) {
				activeTabRef.current = '1';
				setActiveTab('1');
			} else {
				navigate('/');
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [session, accessVersion]);

	// Derive tab from URL on first render
	useEffect(() => {
		const tabFromPath = Object.entries(TAB_ROUTES)
			.sort((a, b) => b[1].length - a[1].length) // Sort by descending path length
			.find(([_, path]) => location.pathname.startsWith(path))?.[0];

		if (tabFromPath && tabFromPath !== activeTab) {
			activeTabRef.current = tabFromPath;
			setActiveTab(tabFromPath);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.pathname]);

	useEffect(() => {
		if (activeTab !== '') {
			if (activeTabRef.current !== activeTab) {
				const currentPath = location.pathname;
				if (TAB_ROUTES[activeTab] && currentPath !== TAB_ROUTES[activeTab]) {
					navigate(TAB_ROUTES[activeTab]); // Ensure navigation to the correct path
				} else {
					navigate('/dashboard'); // Default to Dashboard if no match
				}
			}
		}
	}, [activeTab]);


	// useEffect(() => {
	// 	navigate("/pos");
	// }, []);

	useEffect(() => {
		const handleResize = () => {
			// You can adjust this breakpoint as needed (Bootstrap sm = 576px)
			if (window.innerWidth < 768) {
				setSideMenuMinimized(true);
			} else {
				setSideMenuMinimized(false);
			}
		};

		// Run once on mount
		handleResize();

		// Listen to resize events
		window.addEventListener('resize', handleResize);

		// Cleanup listener
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	const handleTabChange = (tab: string, path?: string) => {
		setActiveTab(tab);
		if (path) navigate(path);
	};


	const handleLogout = () => {
		void logout();
	};

	const handleViewProfile = () => {
		// Navigate to profile page or show modal
	};

	const checkPermission = (menuKey: string) => {
		if (menuKey === '0') {
			if (canSeeDashboard(access)) setActiveTab(menuKey);
			return;
		}
		if (menuKey === '12') {
			if (canAccessReportsHub(access)) setActiveTab(menuKey);
			return;
		}
		if (menuKey === '20') {
			if (canAccessPeopleHub(access)) setActiveTab(menuKey);
			return;
		}
		if (menuKey === '21') {
			if (canAccessCashHub(access)) setActiveTab(menuKey);
			return;
		}
		if (menuKey === '22') {
			if (canAccessStockHub(access)) setActiveTab(menuKey);
			return;
		}
		if (allows(principal, TAB_GRANTS[menuKey])) {
			setActiveTab(menuKey);
		}
	}

	const adminAccess = session && session.role < 3;
	/** Active POS terminal session: hide app chrome for a true full-viewport POS. */
	const isPosSession = activeTab === "1.1";


	const app = (
		<>
			<Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
				<Box className='header-container'>
					<TopBar
						onLogout={handleLogout}
						onViewProfile={handleViewProfile}
						sideMenuMinimized={sideMenuMinimized}
						setSideMenuMinimized={isPosSession ? undefined : setSideMenuMinimized}
						pageTitle={TAB_TITLES[activeTab]}
						posMode={isPosSession}
					/>
				</Box>

				<Box className='body-container'>
					{!isPosSession && (
					<Box
						className={`side-menu-container ${sideMenuMinimized ? 'minimized' : ''}`}
						sx={{
							width: sideMenuMinimized ? 'var(--sidebar-collapsed-width)' : 'var(--sidebar-expanded-width)',
							flexShrink: 0,
							flexBasis: sideMenuMinimized ? 'var(--sidebar-collapsed-width)' : 'var(--sidebar-expanded-width)',
							maxWidth: sideMenuMinimized ? 'var(--sidebar-collapsed-width)' : 'var(--sidebar-expanded-width)',
						}}
					>
						{/* <div className="d-flex justify-content-end mb-2">
							<MoleButton
								variant="ghost"
								size="sm"
								shape="default"
								className="toggle-button"
								aria-label={t("common:ui.tooltips.openMenu")}
								icon={<FontAwesomeIcon icon={sideMenuMinimized ? faAnglesRight : faAnglesLeft} />}
								onClick={() => {
									setSideMenuMinimized(!sideMenuMinimized);
								}}
							>
							</MoleButton>
						</div> */}
						<nav className="mt-3 side-menu">
							{canViewDashboard && (
								<NavItemLink
									icon={faChartLine}
									text={TAB_TITLES['0']}
									tabKey="0"
									activeTab={activeTab}
									setActiveTab={checkPermission}
									showText={!sideMenuMinimized}
								/>
							)}
							{allows(principal, TAB_GRANTS['1']) && (
								<NavItemLink
									icon={faCashRegister}
									text={TAB_TITLES['1']}
									tabKey="1"
									activeTab={activeTab}
									setActiveTab={checkPermission}
									showText={!sideMenuMinimized}
								/>
							)}
							{allows(principal, TAB_GRANTS['4']) && (
								<NavItemLink
									icon={faSackDollar}
									text={TAB_TITLES['4']}
									tabKey="4"
									activeTab={activeTab}
									setActiveTab={checkPermission}
									showText={!sideMenuMinimized}
								/>
							)}
							{allows(principal, TAB_GRANTS['3']) && (
								<NavItemLink
									icon={faMoneyBill}
									text={TAB_TITLES['3']}
									tabKey="3"
									activeTab={activeTab}
									setActiveTab={checkPermission}
									showText={!sideMenuMinimized}
								/>
							)}
							{canViewPeople && (
								<NavItemLink
									icon={faPeopleGroup}
									text={TAB_TITLES['20']}
									tabKey="20"
									activeTab={activeTab}
									setActiveTab={checkPermission}
									selected={PEOPLE_TABS.includes(activeTab)}
									showText={!sideMenuMinimized}
								/>
							)}
							{canViewCash && (
								<NavItemLink
									icon={faVault}
									text={TAB_TITLES['21']}
									tabKey="21"
									activeTab={activeTab}
									setActiveTab={checkPermission}
									selected={CASH_TABS.includes(activeTab)}
									showText={!sideMenuMinimized}
								/>
							)}
							{canViewStock && (
								<NavItemLink
									icon={faCubesStacked}
									text={TAB_TITLES['22']}
									tabKey="22"
									activeTab={activeTab}
									setActiveTab={checkPermission}
									selected={STOCK_TABS.includes(activeTab)}
									showText={!sideMenuMinimized}
								/>
							)}
							{canViewReports && (
								<NavItemLink
									icon={faFileContract}
									text={TAB_TITLES['12']}
									tabKey="12"
									activeTab={activeTab}
									setActiveTab={checkPermission}
									selected={activeTab === '12' || REPORT_LIVE_TABS.includes(activeTab)}
									showText={!sideMenuMinimized}
								/>
							)}
						</nav>
					</Box>
					)}
					<Box
						className={`content-container ${sideMenuMinimized ? 'nav-minimized' : ''}${isPosSession ? ' pos-fullscreen' : ''}`}
						sx={{
							flex: 1,
							overflow: 'hidden',
							...(isPosSession
								? {
									flexBasis: '100%',
									maxWidth: '100%',
									width: '100%',
								}
								: {
									flexBasis: sideMenuMinimized
										? 'calc(100% - var(--sidebar-collapsed-width))'
										: 'calc(100% - var(--sidebar-expanded-width))',
									maxWidth: sideMenuMinimized
										? 'calc(100% - var(--sidebar-collapsed-width))'
										: 'calc(100% - var(--sidebar-expanded-width))',
								}),
						}}
					>
						<div className="tab-content" style={isPosSession || activeTab === '10.3' ? { overflow: 'hidden', height: '100%' } : undefined}>
							<div className="tab-pane" id="tab-0" style={{ display: activeTab === "0" ? "block" : "none", height: "100%" }}>
								{activeTab === "0" && <Helmetify Component={Dashboard} title={TAB_TITLES['0']} />}
							</div>
							<div className="tab-pane" id="tab-1" style={{ display: activeTab === "1" ? "block" : "none", height: "100%" }}>
								{activeTab === "1" && <Helmetify Component={POSGrid} title={TAB_TITLES['1']} />}
							</div>
							<div className="tab-pane" id="tab-1.1" style={{ display: activeTab === "1.1" ? "block" : "none", height: "100%" }}>
								{activeTab === "1.1" && <SaleProvider><Helmetify Component={PointOfSale} title={TAB_TITLES['1.1']} /></SaleProvider>}
							</div>
							<div className="tab-pane" id="tab-2" style={{ display: activeTab === "2" ? "block" : "none", height: "100%" }}>
								{activeTab === "2" && <Helmetify Component={InvoiceSituation} title={TAB_TITLES['2']} />}
							</div>
							<div className="tab-pane" id="tab-3" style={{ display: activeTab === "3" ? "block" : "none", height: "100%" }}>
								{activeTab === "3" && <Helmetify Component={Payment} title={TAB_TITLES['3']} />}
							</div>
							<div className="tab-pane" id="tab-3.1" style={{ display: activeTab === "3.1" ? "block" : "none", height: "100%" }}>
								{activeTab === "3.1" && <Helmetify Component={PaymentModeDaily} title={TAB_TITLES['3.1']} />}
							</div>
							<div className="tab-pane" id="tab-3.2" style={{ display: activeTab === "3.2" ? "block" : "none", height: "100%" }}>
								{activeTab === "3.2" && <Helmetify Component={PaymentTypeDaily} title={TAB_TITLES['3.2']} />}
							</div>
							<div className="tab-pane" id="tab-3.3" style={{ display: activeTab === "3.3" ? "block" : "none", height: "100%" }}>
								{activeTab === "3.3" && <Helmetify Component={PaymentUserDaily} title={TAB_TITLES['3.3']} />}
							</div>
							<div className="tab-pane" id="tab-3.4" style={{ display: activeTab === "3.4" ? "block" : "none", height: "100%" }}>
								{activeTab === "3.4" && <Helmetify Component={PaymentDepotDaily} title={TAB_TITLES['3.4']} />}
							</div>
							<div className="tab-pane" id="tab-15" style={{ display: activeTab === "15" ? "block" : "none", height: "100%" }}>
								{activeTab === "15" && <Helmetify Component={SupplyPayment} title={TAB_TITLES['15']} />}
							</div>
							<div className="tab-pane" id="tab-15.1" style={{ display: activeTab === "15.1" ? "block" : "none", height: "100%" }}>
								{activeTab === "15.1" && <Helmetify Component={SupplyPaymentModeDaily} title={TAB_TITLES['15.1']} />}
							</div>
							<div className="tab-pane" id="tab-15.2" style={{ display: activeTab === "15.2" ? "block" : "none", height: "100%" }}>
								{activeTab === "15.2" && <Helmetify Component={SupplyPaymentUserDaily} title={TAB_TITLES['15.2']} />}
							</div>
							<div className="tab-pane" id="tab-15.3" style={{ display: activeTab === "15.3" ? "block" : "none", height: "100%" }}>
								{activeTab === "15.3" && <Helmetify Component={SupplyPaymentDepotDaily} title={TAB_TITLES['15.3']} />}
							</div>
							<div className="tab-pane" id="tab-17" style={{ display: activeTab === "17" ? "block" : "none", height: "100%" }}>
								{activeTab === "17" && <Helmetify Component={Expense} title={TAB_TITLES['17']} />}
							</div>
							<div className="tab-pane" id="tab-18" style={{ display: activeTab === "18" ? "block" : "none", height: "100%" }}>
								{activeTab === "18" && <Helmetify Component={Voucher} title={TAB_TITLES['18']} />}
							</div>
							<div className="tab-pane" id="tab-19" style={{ display: activeTab === "19" ? "block" : "none", height: "100%" }}>
								{activeTab === "19" && <Helmetify Component={Offer} title={TAB_TITLES['19']} />}
							</div>
							<div className="tab-pane" id="tab-4" style={{ display: activeTab === "4" ? "block" : "none", height: "100%" }}>
								{activeTab === "4" && <Helmetify Component={Sale} title={TAB_TITLES['4']} />}
							</div>
							<div className="tab-pane" id="tab-4.1" style={{ display: activeTab === "4.1" ? "block" : "none", height: "100%" }}>
								{activeTab === "4.1" && <Helmetify Component={SalesItemDaily} title={TAB_TITLES['4.1']} />}
							</div>
							<div className="tab-pane" id="tab-4.2" style={{ display: activeTab === "4.2" ? "block" : "none", height: "100%" }}>
								{activeTab === "4.2" && <Helmetify Component={SalesFamilyDaily} title={TAB_TITLES['4.2']} />}
							</div>
							<div className="tab-pane" id="tab-4.3" style={{ display: activeTab === "4.3" ? "block" : "none", height: "100%" }}>
								{activeTab === "4.3" && <Helmetify Component={SalesCategoryDaily} title={TAB_TITLES['4.3']} />}
							</div>
							<div className="tab-pane" id="tab-4.4" style={{ display: activeTab === "4.4" ? "block" : "none", height: "100%" }}>
								{activeTab === "4.4" && <Helmetify Component={SalesDepotDaily} title={TAB_TITLES['4.4']} />}
							</div>
							<div className="tab-pane" id="tab-4.5" style={{ display: activeTab === "4.5" ? "block" : "none", height: "100%" }}>
								{activeTab === "4.5" && <Helmetify Component={SalesSectorDaily} title={TAB_TITLES['4.5']} />}
							</div>
							<div className="tab-pane" id="tab-4.6" style={{ display: activeTab === "4.6" ? "block" : "none", height: "100%" }}>
								{activeTab === "4.6" && <Helmetify Component={SalesUserDaily} title={TAB_TITLES['4.6']} />}
							</div>
							<div className="tab-pane" id="tab-5" style={{ display: activeTab === "5" ? "block" : "none", height: "100%" }}>
								{activeTab === "5" && <Helmetify Component={ClientSituation} title={TAB_TITLES['5']} />}
							</div>
							<div className="tab-pane" id="tab-5.1" style={{ display: activeTab === "5.1" ? "block" : "none", height: "100%" }}>
								{activeTab === "5.1" && <Helmetify Component={WalletList} title={TAB_TITLES['5.1']} />}
							</div>
							<div className="tab-pane" id="tab-5.3" style={{ display: activeTab === "5.3" ? "block" : "none", height: "100%" }}>
								{activeTab === "5.3" && <Helmetify Component={AddressBook} title={TAB_TITLES['5.3']} />}
							</div>
							<div className="tab-pane" id="tab-5.2" style={{ display: activeTab === "5.2" ? "block" : "none", height: "100%" }}>
								{activeTab === "5.2" && <Helmetify Component={Wallet} title={TAB_TITLES['5.2']} />}
							</div>
							<div className="tab-pane" id="tab-13" style={{ display: activeTab === "13" ? "block" : "none", height: "100%" }}>
								{activeTab === "13" && <Helmetify Component={AgentSituation} title={TAB_TITLES['13']} />}
							</div>
							<div className="tab-pane" id="tab-14" style={{ display: activeTab === "14" ? "block" : "none", height: "100%" }}>
								{activeTab === "14" && <Helmetify Component={SupplierSituation} title={TAB_TITLES['14']} />}
							</div>
							<div className="tab-pane" id="tab-6" style={{ display: activeTab === "6" ? "block" : "none", height: "100%" }}>
								{activeTab === "6" && <Helmetify Component={Deposit} title={TAB_TITLES['6']} />}
							</div>
							<div className="tab-pane" id="tab-7" style={{ display: activeTab === "7" ? "block" : "none", height: "100%" }}>
								{activeTab === "7" && <Helmetify Component={Pending} title={TAB_TITLES['7']} />}
							</div>
							<div className="tab-pane" id="tab-8" style={{ display: activeTab === "8" ? "block" : "none", height: "100%" }}>
								{activeTab === "8" && <MoveProvider><Helmetify Component={MovementRecord} title={TAB_TITLES['8']} /></MoveProvider>}
							</div>
							<div className="tab-pane" id="tab-8.1" style={{ display: activeTab === "8.1" ? "block" : "none", height: "100%" }}>
								{activeTab === "8.1" && <MoveProvider><Helmetify Component={MovementListing} title={TAB_TITLES['8.1']} /></MoveProvider>}
							</div>
							<div className="tab-pane" id="tab-8.2" style={{ display: activeTab === "8.2" ? "block" : "none", height: "100%" }}>
								{activeTab === "8.2" && <MoveProvider><Helmetify Component={MovementTracing} title={TAB_TITLES['8.2']} /></MoveProvider>}
							</div>
							<div className="tab-pane" id="tab-8.3" style={{ display: activeTab === "8.3" ? "block" : "none", height: "100%" }}>
								{activeTab === "8.3" && <MoveProvider><Helmetify Component={MovementTracingChrono} title={TAB_TITLES['8.3']} /></MoveProvider>}
							</div>
							<div className="tab-pane" id="tab-9" style={{ display: activeTab === "9" ? "block" : "none", height: "100%" }}>
								{activeTab === "9" && <SupplyProvider><Helmetify Component={SupplyRecord} title={TAB_TITLES['9']} /></SupplyProvider>}
							</div>
							<div className="tab-pane" id="tab-9.1" style={{ display: activeTab === "9.1" ? "block" : "none", height: "100%" }}>
								{activeTab === "9.1" && <SupplyProvider><Helmetify Component={SupplyListing} title={TAB_TITLES['9.1']} /></SupplyProvider>}
							</div>
							<div className="tab-pane" id="tab-9.2" style={{ display: activeTab === "9.2" ? "block" : "none", height: "100%" }}>
								{activeTab === "9.2" && <Helmetify Component={SupplySituation} title={TAB_TITLES['9.2']} />}
							</div>
							<div className="tab-pane" id="tab-10" style={{ display: activeTab === "10" ? "block" : "none", height: "100%" }}>
								{activeTab === "10" && <InventoryProvider><Helmetify Component={InventoryRecord} title={TAB_TITLES['10']} /></InventoryProvider>}
							</div>
							<div className="tab-pane" id="tab-10.1" style={{ display: activeTab === "10.1" ? "block" : "none", height: "100%" }}>
								{activeTab === "10.1" && <Helmetify Component={CurrentInventory} title={TAB_TITLES['10.1']} />}
							</div>
							<div className="tab-pane" id="tab-10.2" style={{ display: activeTab === "10.2" ? "block" : "none", height: "100%" }}>
								{activeTab === "10.2" && <Helmetify Component={CurrentInventoryVisual} title={TAB_TITLES['10.2']} />}
							</div>
							<div
								className="tab-pane"
								id="tab-10.3"
								style={{
									display: activeTab === "10.3" ? "flex" : "none",
									flexDirection: "column",
									height: "100%",
									minHeight: 0,
									overflow: "hidden",
								}}
							>
								{/* Wrapper supplies a real flex child (Helmetify uses a fragment). */}
								{activeTab === "10.3" && (
									<div style={{ flex: 1, minHeight: 0, height: "100%", overflow: "hidden" }}>
										<Helmetify Component={ProductCatalog} title={TAB_TITLES['10.3']} />
									</div>
								)}
							</div>
							<div className="tab-pane" id="tab-11" style={{ display: activeTab === "11" ? "block" : "none", height: "100%" }}>
								{activeTab === "11" && <ConversionProvider><Helmetify Component={ConversionRecord} title={TAB_TITLES['11']} /></ConversionProvider>}
							</div>
							<div className="tab-pane" id="tab-16" style={{ display: activeTab === "16" ? "block" : "none", height: "100%" }}>
								{activeTab === "16" && <ProductionProvider><Helmetify Component={ProductionRecords} title={TAB_TITLES['16']} /></ProductionProvider>}
							</div>
							<div className="tab-pane" id="tab-16.1" style={{ display: activeTab === "16.1" ? "block" : "none", height: "100%" }}>
								{activeTab === "16.1" && <Helmetify Component={BomRecords} title={TAB_TITLES['16.1']} />}
							</div>
							<div className="tab-pane" id="tab-12" style={{ display: activeTab === "12" ? "block" : "none", height: "100%" }}>
								{activeTab === "12" && <Helmetify Component={ReportsDashboard} title={TAB_TITLES['12']} />}
							</div>
							<div className="tab-pane" id="tab-20" style={{ display: activeTab === "20" ? "block" : "none", height: "100%" }}>
								{activeTab === "20" && <Helmetify Component={PeopleHub} title={TAB_TITLES['20']} />}
							</div>
							<div className="tab-pane" id="tab-21" style={{ display: activeTab === "21" ? "block" : "none", height: "100%" }}>
								{activeTab === "21" && <Helmetify Component={CashHub} title={TAB_TITLES['21']} />}
							</div>
							<div className="tab-pane" id="tab-22" style={{ display: activeTab === "22" ? "block" : "none", height: "100%" }}>
								{activeTab === "22" && <Helmetify Component={StockHub} title={TAB_TITLES['22']} />}
							</div>
						</div>
					</Box>
				</Box>
				<Box className="footer-container">
					<BottomBar />
				</Box>
				{!connected && !loginModalOpen && <OfflineScreen />}
			</Box>
		</>
	);
	return app;
}

export default Main;
